import EditableImage from "../../../../components/EditableImage"
import useModal from "../../../../hooks/useModal"
import useImage from "../../../../hooks/useImage"
import useMultimedia from "../../../../hooks/useMultimedia"
import useRwas from "../../../../hooks/useRwas"
import useCompany from "../../../../hooks/useCompany"
import { Button } from "@oc-labs/wt-storybook"
import RWADataRow from "../components/RWADataRow"

export default function WineBottle({ rwa, reloadRwa, deleteRwa }) {
  const { activeCompany: company } = useCompany();
  const { open } = useModal("rwa-edit-param-modal")
  const { convertToBlob } = useImage();
  const { upload } = useMultimedia();
  const { updateRwa } = useRwas();
  const data = rwa?.data

  const handleUploadImage = async (newImage, key) => {
    if (!company || !company.id) {
      toast.error("No company selected. Cannot upload image.");
      return;
    }
    let fileToUpload = newImage;
    if (!(newImage instanceof File) && !(newImage instanceof Blob)) {
      const { blob, contentType } = await convertToBlob(newImage);
      fileToUpload = new File([blob], "image.png", { type: contentType });
    }
    const fileUrl = await upload(fileToUpload);
    await updateRwa(company.id, rwa.id, {
      params: {
        [key]: { value: fileUrl, type: "image" }
      },
      name: rwa?.name,
      type: rwa?.type,
    });
    reloadRwa && reloadRwa();
  }
  
  return (
    <div className="p-4 space-y-6 mb-4">

      {/* First Row: Image + Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Wine Image */}
        <div className="md:col-span-1">
          <EditableImage
            imageClassName="max-h-[400px] object-cover"
            className="h-full"
            image={data?.image?.value}
            onImageChange={(newImage) => handleUploadImage(newImage, "image")}
          />
        </div>

        {/* Basic Info */}
        <div className="md:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <RWADataRow
              field={data?.grapeVariety}
              title="Grape Variety"
              param="grapeVariety"
            />

            <RWADataRow
              field={data?.vintage}
              title="Vintage"
              param="vintage"
            />

            <RWADataRow
              field={data?.wineryName}
              title="Winery Name"
              param="wineryName"
            />

            <RWADataRow
              field={data?.vineyardOwner}
              title="Vineyard Owner"
              param="vineyardOwner"
            />
          </div>
        </div>
      </div>

      {/* Second Row: Map + Vineyard Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Map Section */}
        
        <div className="md:col-span-1 rounded-lg p-4">
          <RWADataRow
            field={data?.theWinery}
            param="theWinery"
            title="Winery location"
            bordered={false}
          />

          <Button
            variant="secondary"
            size="small"
            className="w-full mt-2"
            onClick={() => open("theWinery")}
          >
            Edit winery location
          </Button>
        </div>

        {/* Vineyard Details */}
        <div className="md:col-span-2 p-2">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-medium text-gray-900">Vineyard details</h3>
          </div>

          <div className="border-t mb-4" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            {/* Altitude */}
            <RWADataRow
              field={data?.altitudeOfVineyard}
              param="altitudeOfVineyard"
              title="Altitude"
            />

            <RWADataRow
              field={data?.harvestDate}
              param="harvestDate"
              title="Harvest Date"
            />

            <RWADataRow
              field={data?.bottlingDate}
              param="bottlingDate"
              title="Bottling Date"
            />

            <RWADataRow
              field={data?.soilType}
              param="soilType"
              title="Soil Type"
            />

          </div>
        </div>
      </div>

      {/* First Row: Style & Structure + About */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* About */}
         <RWADataRow
          field={data?.about}
          param="about"
          title="About"
        />
        
        {/* Style & Structure */}
        <RWADataRow
          field={data?.styleAndStructure}
          param="styleAndStructure"
          title="Style & Structure"
        />
      </div>

      {/* Second Row: Terroir + Tasting Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* Terroir */}
        <RWADataRow
          field={data?.terroirInfo}
          param="terroirInfo"
          title="Terroir"
        />
        <RWADataRow
          field={data?.tastingNotes}
          param="tastingNotes"
          title="Tasting Notes"
        />
      </div>

      {/* Third Row: Food Pairings + Serving Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* Food Pairings */}
        <RWADataRow
          field={data?.foodPairings}
          param="foodPairings"
          title="Food Pairings"
        />
        <RWADataRow
          field={data?.servingRecommendations}
          param="servingRecommendations"
          title="Serving Recommendations"
        />
      </div>
    </div>
  )
}
